// @ts-nocheck

import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';

import useMergeProps from '../../hooks/useMergeProps';
import { selectEntitiesMatchesEntity } from '../../selectors/entities/matches/entitiesMatchesPrimary.selector';
import { selectHistoryEntitiesMatchesEntity } from '../../selectors/history/historyPrimary.selector';
import { MatchEntity, MatchEntityStatus } from '../../states/entities/matches';
import type { ScoreboardProps } from '../../states/scoreboards';
import { type CategoryId, CategoryIds } from '../../types/categories';
import { type SportId, SportIds } from '../../types/sports';
import selectors from '../../utils/scoreboards';

import HistoryMatchFullTitle from './HistoryMatchFullTitle';

const getSportConfigByCategoryId = (categoryId, isEnded) => {
  switch (categoryId) {
    case CategoryIds.ESPORT_COUNTER_STRIKE: {
      return { selector: selectors.esportCounterStrike };
    }
    case CategoryIds.ESPORT_SOCCER:
      return { selector: selectors.soccer };
    case CategoryIds.ESPORT_BASKETBALL:
      return { isSetLayout: !isEnded, selector: selectors.basketballAndFootball };
    case CategoryIds.ESPORT_ICE_HOCKEY:
      return { isSetLayout: !isEnded, selector: selectors.hockey };
    default:
      return { selector: selectors.defaultSelector };
  }
};
const getSportConfig = (sportId, categoryId, isEnded) => {
  switch (sportId) {
    case SportIds.BADMINTON:
    case SportIds.BEACH_VOLLEY:
    case SportIds.SQUASH:
    case SportIds.TABLE_TENNIS:
    case SportIds.VOLLEYBALL:
      return { isSetLayout: true, selector: selectors.lastSetAsGameScore };
    case SportIds.CRICKET:
      return { isSetLayout: true, selector: selectors.snooker };
    case SportIds.SNOOKER:
      return { selector: selectors.snooker };
    case SportIds.PADEL:
    case SportIds.TENNIS:
      return { isSetLayout: true, selector: selectors.tennis };
    case SportIds.SOCCER:
      return { selector: selectors.soccer };
    case SportIds.FOOTBALL:
    case SportIds.ESPORT_BASKETBALL:
    case SportIds.BASKETBALL:
      return { isSetLayout: !isEnded, selector: selectors.basketballAndFootball };
    case SportIds.BASEBALL:
      return { selector: selectors.baseball };
    case SportIds.AUSSIE_RULES:
      return { selector: selectors.australianFootball };
    case SportIds.ICEHOCKEY:
    case SportIds.ESPORT_ICE_HOCKEY:
      return { isSetLayout: !isEnded, selector: selectors.hockey };
    case SportIds.RUGBY_7S:
    case SportIds.RUGBY_LEAGUE:
    case SportIds.RUGBY_7S_OLD:
    case SportIds.RUGBY_LEAGUE_OLD:
    case SportIds.RUGBY:
      return { selector: selectors.rugby };
    case SportIds.ESPORT:
      return getSportConfigByCategoryId(categoryId, isEnded);
    case SportIds.WATERPOLO:
      return { selector: selectors.waterpolo };
    default:
      return { selector: selectors.defaultSelector };
  }
};

type HistoryMatchFullTitleContainerProps = ScoreboardProps & {
  sportId: SportId;
  categoryId: CategoryId;
};

const HistoryMatchFullTitleContainer = (ownProps: HistoryMatchFullTitleContainerProps) => {
  const { matchId, sportId, categoryId } = ownProps;

  const historyMatch = useSelector((state) => selectHistoryEntitiesMatchesEntity(state, { matchId }));
  const globalStateMatch = useSelector((state) => selectEntitiesMatchesEntity(state, { matchId }));

  // $FlowFixMe
  const match = useMemo(
    () => ({ ...(historyMatch ?? {}), ...(globalStateMatch ?? {}) }),
    [historyMatch, globalStateMatch],
  );
  const isEnded = useMemo(() => MatchEntity.isEnded(match), [match]);

  const config = getSportConfig(sportId, categoryId, isEnded);

  const selectedProps = config.selector(match, ownProps.fromBetBuilder, isEnded);

  if (isEnded && match.sportId === SportIds.TENNIS) {
    selectedProps.scores.main.score = ['-', '-'];
  }

  const otherProps = useMemo(
    () => ({
      isLive: MatchEntityStatus.isLive(match.status),
      isEnded,
      isSetLayout: config.isSetLayout ? true : false,
    }),
    [match, isEnded, config],
  );

  const enhancedProps = useMergeProps({ ownProps, selectedProps, otherProps });

  return <HistoryMatchFullTitle {...enhancedProps} />;
};

export default HistoryMatchFullTitleContainer;
